import React, { useState } from 'react';
import {
  Box,
  Paper,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Badge,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import AddIcon from '@mui/icons-material/Add';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RuleIcon from '@mui/icons-material/Rule';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import StorageIcon from '@mui/icons-material/Storage';
import { useTranslation } from 'react-i18next';
import { erzwungenerStatusfilter } from '../utils/helpers';

const FilterBarContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  // AK4 (#274, Feste Breite unabhaengig von der Tabelle darunter) haengt
  // nicht an dieser Komponente: `maxWidth: '100%'` waere hier wirkungslos,
  // ein Paper ist ohnehin blockweit (Review Runde 1, B2). Die Begrenzung
  // sitzt eine Ebene hoeher in MainLayout (`minWidth: 0` auf der
  // Hauptflaeche, siehe MainLayout.js/MainLayout.test.js) -- ohne die kann
  // eine breitere Tabelle im selben Flex-Container die Seite und damit auch
  // diese Leiste mitziehen.
}));

const TopRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

const ActiveFiltersRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  minHeight: '32px',
}));

/**
 * Compact Filter Bar Component
 * Modern, space-efficient filter interface with progressive disclosure
 *
 * Features:
 * - Integrated search field (left side)
 * - Sets dropdown for quick filter combinations
 * - "+ Filter hinzufügen" button to toggle expansion panel
 * - AI Assistant button for intelligent filtering
 * - Name view toggle (Lokal, OMOP, Beide) - right aligned
 * - Active filters displayed as removable chips (including AI suggestions)
 * - ~120px height vs ~600px of old design
 *
 * @param {string} searchTerm - Current search term
 * @param {Function} setSearchTerm - Setter for search term
 * @param {Array} savedSets - Available filter sets
 * @param {Function} handleLoadSet - Handler for loading a saved set
 * @param {Function} getSetTypeBadge - Get badge label for set type
 * @param {Array} selectedTags - Selected tag filters
 * @param {Array} selectedStations - Selected station filters
 * @param {string} selectedSensitivity - Selected sensitivity filter
 * @param {Function} setSelectedTags - Setter for tags
 * @param {Function} setSelectedStations - Setter for stations
 * @param {Function} setSelectedSensitivity - Setter for sensitivity
 * @param {Array} aiSuggestedParameterIds - AI-suggested parameter IDs
 * @param {Function} setAiSuggestedParameterIds - Setter for AI suggestions
 * @param {boolean} onlyUnvalidatedAiSuggestions - AK4-Arbeitsliste: nur ungeprüfte KI-Vorschläge (#257)
 * @param {Function} setOnlyUnvalidatedAiSuggestions - Setter dafür
 * @param {Array} parameters - All parameters (to get names for AI chips)
 * @param {boolean} filterPanelOpen - Whether filter expansion panel is open
 * @param {Function} setFilterPanelOpen - Toggle filter expansion panel
 * @param {Function} setAiAssistantOpen - Open AI assistant dialog
 * @param {string} nameView - Current name view mode (charite, omop, both)
 * @param {Function} setNameView - Setter for name view mode
 * @param {Function} clearAllFilters - Handler to clear all filters
 */
const CompactFilterBar = ({
  searchTerm,
  setSearchTerm,
  savedSets,
  handleLoadSet,
  getSetTypeBadge,
  selectedTags,
  selectedStations,
  selectedSensitivity,
  selectedOmopStatus,
  selectedOpenehrStatus,
  setSelectedOpenehrStatus,
  selectedSourceSystems,
  selectedArchetype,
  setSelectedArchetype,
  aiSuggestedParameterIds,
  onlyUnvalidatedAiSuggestions,
  setParameterIds,
  setSelectedTags,
  setSelectedStations,
  setSelectedSensitivity,
  setSelectedOmopStatus,
  setSelectedSourceSystems,
  setAiSuggestedParameterIds,
  setOnlyUnvalidatedAiSuggestions,
  setSetParameterIds,
  parameters,
  getParameterName,
  filterPanelOpen,
  setFilterPanelOpen,
  setAiAssistantOpen,
  nameView,
  setNameView,
  clearAllFilters,
}) => {
  const { t } = useTranslation();
  const erzwungen = erzwungenerStatusfilter(nameView);
  // Menu anchor for sets
  const [setsMenuAnchor, setSetsMenuAnchor] = useState(null);

  // #311 Review Runde 1, Blocker 1: das Suchfeld erzwang minWidth:'300px'
  // unabhaengig von der Fensterbreite. Bei 375px bleiben in dieser Leiste
  // (content-area 1rem + Container-Gutter 16px + FilterBarContainer-Padding
  // 16px, je Seite) nur noch 279px -- 21px zu wenig fuer die 300px, TopRow's
  // eigenes flexWrap hilft nicht, weil ein einzelnes Flex-Item nicht unter
  // seine minWidth schrumpft, egal ob die Zeile umbricht. Unterhalb `sm`
  // (600px) reicht selbst der Container-Gutter-Wechsel (16px->24px bei sm)
  // nicht aus, um 300px unterzubringen (Grenzwert liegt rechnerisch bei
  // rund 396px) -- deshalb 0 als Mindestbreite unterhalb `sm`, unveraendert
  // 300px ab `sm` (dort passen 300px mit klarem Abstand: 488px verfuegbar
  // bei genau 600px). `useMediaQuery` statt eines `sx`-Breakpoint-Objekts,
  // aus demselben Grund wie in DataCatalog.js/#311 Runde 1: jsdom wertet
  // @media-Regeln aus Emotion/MUI-Stylesheets fuer getComputedStyle nicht
  // aus, der aufgeloeste JS-Wert schon.
  const theme = useTheme();
  const istSchmalesFenster = useMediaQuery(theme.breakpoints.down('sm'));

  // Calculate active filter count
  const activeFilterCount =
    selectedTags.length +
    selectedStations.length +
    (selectedSensitivity && selectedSensitivity !== 'all' ? 1 : 0) +
    // Der erzwungene Filter zaehlt mit: er schraenkt die Liste ein, also
    // gehoert er in die Zeile der aktiven Filter -- sonst verschwiegen wir
    // genau die Einschraenkung, die gerade wirkt (Review Runde 1, B1).
    (erzwungen.omopStatus || (selectedOmopStatus && selectedOmopStatus !== 'all') ? 1 : 0) +
    (erzwungen.openehrStatus || (selectedOpenehrStatus && selectedOpenehrStatus !== 'all') ? 1 : 0) +
    (selectedSourceSystems ? selectedSourceSystems.length : 0) +
    // `null` heisst "kein Chip gewaehlt" (#788) -- der Bucket "ohne Zuordnung"
    // ist selbst ein waehlbarer Chip mit dem Wert `''` und muss darum ueber
    // `!== null` gezaehlt werden, nicht ueber Wahrheitswert (sonst zaehlte
    // "ohne Zuordnung" als nicht aktiv).
    (selectedArchetype !== null && selectedArchetype !== undefined ? 1 : 0) +
    (aiSuggestedParameterIds ? aiSuggestedParameterIds.length : 0) +
    (setParameterIds ? setParameterIds.length : 0) +
    (onlyUnvalidatedAiSuggestions ? 1 : 0);

  // Handlers for menus
  const handleOpenSetsMenu = (event) => {
    setSetsMenuAnchor(event.currentTarget);
  };

  const handleCloseSetsMenu = () => {
    setSetsMenuAnchor(null);
  };

  const handleLoadSetClick = (set) => {
    handleLoadSet(set);
    handleCloseSetsMenu();
  };

  const handleToggleFilterPanel = () => {
    setFilterPanelOpen(!filterPanelOpen);
  };

  // Remove individual tag
  const handleRemoveTag = (tag) => {
    setSelectedTags(prev => prev.filter(t => t !== tag));
  };

  // Remove individual station
  const handleRemoveStation = (station) => {
    setSelectedStations(prev => prev.filter(s => s !== station));
  };

  // Remove sensitivity filter
  const handleRemoveSensitivity = () => {
    setSelectedSensitivity('all');
  };

  // Remove OMOP status filter
  const handleRemoveOmopStatus = () => {
    setSelectedOmopStatus('all');
  };

  // Remove individual source system
  const handleRemoveSourceSystem = (system) => {
    setSelectedSourceSystems(prev => prev.filter(s => s !== system));
  };

  // Remove individual AI suggested parameter
  const handleRemoveAiSuggestion = (parameterId) => {
    setAiSuggestedParameterIds(prev => prev.filter(id => id !== parameterId));
  };

  // Remove individual set parameter
  const handleRemoveSetParameter = (parameterId) => {
    setSetParameterIds(prev => prev.filter(id => id !== parameterId));
  };

  // Remove the AK4 worklist filter (#257): "nur ungeprüfte KI-Vorschläge"
  const handleRemoveUnvalidatedAiFilter = () => {
    setOnlyUnvalidatedAiSuggestions(false);
  };

  // Remove the Archetyp-Chip filter (#788) -- `null`, nicht `''`: `''` waere
  // wieder eine Auswahl (der Bucket "ohne Zuordnung").
  const handleRemoveArchetype = () => {
    setSelectedArchetype(null);
  };

  // Get parameter name by ID (use prop function if provided)
  const getParamName = getParameterName || ((parameterId) => {
    const param = parameters?.find(p => p.id === parameterId);
    return param ? param.name : `Parameter ${parameterId}`;
  });

  // Get label for sensitivity
  const getSensitivityLabel = (value) => {
    switch(value) {
      case 'plain': return t('dataCatalog.filterPanel.sensitivityOptions.plain.label');
      case 'pseudonymisiert': return t('dataCatalog.sensitivity.levels.pseudonymisiert');
      case 'anonymisiert': return t('dataCatalog.sensitivity.levels.anonymisiert');
      default: return value;
    }
  };

  // Get label for OMOP status
  const getOmopStatusLabel = (value) => {
    switch(value) {
      case 'validated': return t('dataCatalog.omopStatus.validated');
      case 'suggested': return t('dataCatalog.omopStatus.suggested');
      case 'not_mapped': return t('dataCatalog.omopStatus.notMapped');
      default: return value;
    }
  };

  // Filter sets by type (for Sets menu)
  const parameterSets = savedSets?.filter(set => set.type === 'parameters' || set.type === 'mixed') || [];

  return (
    <FilterBarContainer elevation={1}>
      {/* Top Row: Search + Action Buttons */}
      <TopRow>
        {/* Search Field */}
        <TextField
          data-testid="filterbar-suchfeld"
          placeholder={t('dataCatalog.filterBar.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{
            flexGrow: 1,
            minWidth: istSchmalesFenster ? 0 : '300px',
            maxWidth: '600px',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm('')}
                  edge="end"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Sets Dropdown */}
        <Button
          variant="outlined"
          startIcon={<BookmarkIcon />}
          endIcon={<ExpandMoreIcon />}
          onClick={handleOpenSetsMenu}
          size="medium"
        >
          {t('dataCatalog.filterBar.sets')}
        </Button>
        <Menu
          anchorEl={setsMenuAnchor}
          open={Boolean(setsMenuAnchor)}
          onClose={handleCloseSetsMenu}
          PaperProps={{
            sx: { minWidth: 280 }
          }}
        >
          <MenuItem disabled>
            <Typography variant="caption" color="textSecondary">
              {t('dataCatalog.filterBar.savedFilterCombinations')}
            </Typography>
          </MenuItem>
          <Divider />
          {parameterSets.length > 0 ? (
            parameterSets.map(set => (
              <MenuItem key={set.id} onClick={() => handleLoadSetClick(set)}>
                <ListItemIcon>
                  <span style={{ fontSize: '1.2rem' }}>{set.icon}</span>
                </ListItemIcon>
                <ListItemText
                  primary={set.name}
                  secondary={getSetTypeBadge(set.type)}
                />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <Typography variant="body2" color="textSecondary">
                {t('dataCatalog.filterBar.noSetsAvailable')}
              </Typography>
            </MenuItem>
          )}
        </Menu>

        {/* Add Filter Button */}
        <Badge badgeContent={activeFilterCount} color="primary">
          <Button
            variant={filterPanelOpen ? "contained" : "outlined"}
            startIcon={<AddIcon />}
            endIcon={<ExpandMoreIcon sx={{ transform: filterPanelOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.3s' }} />}
            onClick={handleToggleFilterPanel}
            size="medium"
          >
            {t('dataCatalog.filterBar.addFilter')}
          </Button>
        </Badge>

        {/* AI Assistant Button */}
        <Button
          variant="contained"
          size="medium"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => setAiAssistantOpen(true)}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)',
            }
          }}
        >
          {t('dataCatalog.filterBar.aiAssistant')}
        </Button>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* **Ein** Umschalter, vier Stellungen (#782).

            Er waehlt Name **und** Zuordnung in einem: lokal zeigt nur den
            Hausnamen, OMOP und openEHR je eine Darstellung, "alle" beide
            nebeneinander. In den beiden mittleren Stellungen zeigt die Liste
            ausserdem nur, was dort auch zugeordnet ist -- in "alle" nicht,
            und das ist Absicht: nur dort bleibt ein Parameter sichtbar, den
            die eine Welt kennt und die andere nicht. */}
        <ToggleButtonGroup
          value={nameView}
          exclusive
          onChange={(e, neueAnsicht) => neueAnsicht && setNameView(neueAnsicht)}
          size="small"
          aria-label={t('dataCatalog.filterBar.nameView.ariaLabel')}
          sx={{ backgroundColor: 'white' }}
        >
          <ToggleButton value="charite">
            <Tooltip title={t('dataCatalog.filterBar.nameView.localTooltip')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}>
                {t('dataCatalog.filterBar.nameView.local')}
              </Box>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="omop">
            <Tooltip title={t('dataCatalog.filterBar.nameView.omopTooltip')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}>
                {t('dataCatalog.filterBar.nameView.omop')}
              </Box>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="openehr">
            <Tooltip title={t('dataCatalog.filterBar.nameView.openehrTooltip')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}>
                {t('dataCatalog.filterBar.nameView.openehr')}
              </Box>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="alle">
            <Tooltip title={t('dataCatalog.filterBar.nameView.alleTooltip')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}>
                <SwapHorizIcon fontSize="small" />
                {t('dataCatalog.filterBar.nameView.alle')}
              </Box>
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Reset Button */}
        {activeFilterCount > 0 && (
          <Button
            size="small"
            onClick={clearAllFilters}
            color="secondary"
            sx={{ ml: 1 }}
          >
            {t('dataCatalog.filterBar.resetAll')}
          </Button>
        )}
      </TopRow>

      {/* Active Filters Row */}
      {activeFilterCount > 0 && (
        <ActiveFiltersRow>
          <Typography variant="caption" color="textSecondary" sx={{ mr: 0.5 }}>
            {t('dataCatalog.filterBar.activeFilters', { count: activeFilterCount })}
          </Typography>

          {/* Tag Chips */}
          {selectedTags.map(tag => (
            <Chip
              key={`tag-${tag}`}
              label={tag}
              size="small"
              color="primary"
              onDelete={() => handleRemoveTag(tag)}
              icon={<LocalOfferIcon />}
            />
          ))}

          {/* Station Chips */}
          {selectedStations.map(station => (
            <Chip
              key={`station-${station}`}
              label={station}
              size="small"
              color="secondary"
              onDelete={() => handleRemoveStation(station)}
              icon={<LocalHospitalIcon />}
            />
          ))}

          {/* Source System Chips */}
          {selectedSourceSystems && selectedSourceSystems.map(system => (
            <Chip
              key={`system-${system}`}
              label={system}
              size="small"
              color="info"
              onDelete={() => handleRemoveSourceSystem(system)}
              icon={<StorageIcon />}
            />
          ))}

          {/* Archetyp-Chip-Filter (#788): sichtbar und zaehlbar wie jeder
              andere aktive Filter -- sonst wirkte er in der Filterleiste wie
              nicht vorhanden, obwohl er die Liste einschraenkt (Review Runde
              3, Blocker 2). */}
          {selectedArchetype !== null && selectedArchetype !== undefined && (
            <Chip
              label={t('dataCatalog.filterBar.archetypeFilter', {
                value: selectedArchetype || t('dataCatalog.openehr.archetypOhneZuordnung'),
              })}
              size="small"
              color="primary"
              onDelete={handleRemoveArchetype}
            />
          )}

          {/* Set Parameter Chips */}
          {setParameterIds && setParameterIds.map(paramId => (
            <Chip
              key={`set-${paramId}`}
              label={getParamName(paramId)}
              size="small"
              onDelete={() => handleRemoveSetParameter(paramId)}
              icon={<BookmarkIcon />}
              sx={{
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                color: 'white',
                fontWeight: 500,
                '& .MuiChip-icon': {
                  color: 'white',
                },
                '& .MuiChip-deleteIcon': {
                  color: 'white',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.8)',
                  },
                },
              }}
            />
          ))}

          {/* AI Suggested Parameter Chips */}
          {aiSuggestedParameterIds && aiSuggestedParameterIds.map(paramId => (
            <Chip
              key={`ai-${paramId}`}
              label={getParamName(paramId)}
              size="small"
              onDelete={() => handleRemoveAiSuggestion(paramId)}
              icon={<AutoAwesomeIcon />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 500,
                '& .MuiChip-icon': {
                  color: 'white',
                },
                '& .MuiChip-deleteIcon': {
                  color: 'white',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.8)',
                  },
                },
              }}
            />
          ))}

          {/* AK4-Arbeitsliste (#257): "nur ungeprüfte KI-Vorschläge". Bis
              Runde 1 reduzierte dieser Schalter die Liste bereits (verdrahtet
              in useFilterSort ueber katalogBlaetterung), war hier aber weder
              als Chip noch im Zaehler sichtbar -- die Liste wurde kleiner,
              ohne dass die Filterleiste einen Grund dafuer zeigte. */}
          {onlyUnvalidatedAiSuggestions && (
            <Chip
              label={t('dataCatalog.mappingProvenance.filterLabel', 'Nur ungeprüfte KI-Vorschläge')}
              size="small"
              color="warning"
              onDelete={handleRemoveUnvalidatedAiFilter}
              icon={<RuleIcon />}
            />
          )}

          {/* Sensitivity Chip */}
          {selectedSensitivity && selectedSensitivity !== 'all' && (
            <Chip
              label={t('dataCatalog.filterBar.sensitivityFilter', { value: getSensitivityLabel(selectedSensitivity) })}
              size="small"
              color="default"
              onDelete={handleRemoveSensitivity}
              icon={<LockIcon />}
            />
          )}

          {/* OMOP Status Chip */}
          {/* **Der erzwungene Filter bekommt einen eigenen Chip** (Review Runde 1,
              B1). Vorher zeigte die Leiste weiter die Wahl des Nutzers,
              waehrend an die API etwas anderes ging. */}
          {erzwungen.openehrStatus && (
            <Chip
              size="small"
              label={t('dataCatalog.filterBar.openehrFilter', {
                value: t('dataCatalog.filterPanel.zugeordnet'),
              })}
              sx={{ mr: 0.5 }}
            />
          )}
          {erzwungen.omopStatus && (
            <Chip
              size="small"
              label={t('dataCatalog.filterBar.omopFilter', {
                value: t('dataCatalog.filterPanel.zugeordnet'),
              })}
              sx={{ mr: 0.5 }}
            />
          )}
          {!erzwungen.openehrStatus && selectedOpenehrStatus && selectedOpenehrStatus !== 'all' && (
            <Chip
              size="small"
              label={t('dataCatalog.filterBar.openehrFilter', {
                value: getOmopStatusLabel(selectedOpenehrStatus),
              })}
              onDelete={() => setSelectedOpenehrStatus('all')}
              sx={{ mr: 0.5 }}
            />
          )}
          {!erzwungen.omopStatus && selectedOmopStatus && selectedOmopStatus !== 'all' && (
            <Chip
              label={t('dataCatalog.filterBar.omopFilter', { value: getOmopStatusLabel(selectedOmopStatus) })}
              size="small"
              color="default"
              onDelete={handleRemoveOmopStatus}
              icon={<CheckCircleIcon />}
            />
          )}
        </ActiveFiltersRow>
      )}
    </FilterBarContainer>
  );
};

export default CompactFilterBar;
